import { sleep } from './utils';
import {
    listSimulatorClusters,
    SIMULATOR_SVM_LOOKUP_PATH,
    SIMULATOR_VOLUME_LOOKUP_PATH
} from './aiSimulatorAssets';

// ============================================================================
// Import all chat JSON files from data/chat/
// Each file represents a recorded conversation with a unique user prompt.
// To add a new conversation, simply drop a new HAR-recorded JSON file here.
// ============================================================================
import chatAnalyzeCpu from "./data/chat/analyze_cpu.json";
import chatCreateVol from "./data/chat/create_vol.json";
import chatCreateVolDynamicForm from "./data/chat/create_vol_dynamic_form.json";
import chatCreateVolLayoutForm from "./data/chat/create_vol_layout_form.json";
import chatCreateVolClasses from "./data/chat/create_vol_classes.json";
import chatWelcome from "./data/chat/welcome.json";
import chatBlockedContent from "./data/chat/blocked_content.json";
import chatErrorApi from "./data/chat/error_api.json";
import chatWorkloadPerformance from "./data/chat/workload_performance.json";
import chatAlertOverview from "./data/chat/alert_overview.json";
import chatRecommendCluster from "./data/chat/recommend_cluster.json";
import chatVolOver85 from "./data/chat/vol_over_85.json";
import chatNoisyCluster from "./data/chat/noisiest_cluster.json";
import chatStorageClasses from "./data/chat/storage_classes.json";
import chatCreateIgroupForm from "./data/chat/create_igroup_form.json";

/**
 * Registry of all imported chat conversation JSON files.
 * Add new imports above and include them in this array.
 * @type {Array<Array<Object>>}
 */
const allChatRecordings = [
    chatAnalyzeCpu,
    chatCreateVol,
    chatCreateVolDynamicForm,
    chatCreateVolLayoutForm,
    chatCreateVolClasses,
    chatWelcome,
    chatBlockedContent,
    chatErrorApi,
    chatWorkloadPerformance,
    chatAlertOverview,
    chatRecommendCluster,
    chatVolOver85,
    chatNoisyCluster,
    chatStorageClasses,
    chatCreateIgroupForm
];

// ============================================================================
// Multi-Turn Conversation Parsing
// ============================================================================

/**
 * Checks whether a "send" entry is an actual user message (contains a messages array)
 * as opposed to a ping or other control message.
 * @param {Object} entry - A HAR recording entry.
 * @returns {boolean}
 */
const isUserMessage = (entry) => {
    if (entry.type !== 'send') return false;
    try {
        const parsed = JSON.parse(entry.data);
        return !!(parsed.messages && Array.isArray(parsed.messages));
    } catch {
        return false;
    }
};

/**
 * Extracts the last user message content from a send entry's data.
 * In multi-turn conversations, each send contains the full conversation history,
 * so the last user message is the newest prompt for that turn.
 * @param {Object} sendEntry - A "send" HAR entry.
 * @returns {string|null} - The user's message content, or null.
 */
const extractLastUserContent = (sendEntry) => {
    try {
        const parsed = JSON.parse(sendEntry.data);
        const userMessages = parsed.messages?.filter(m => m.role === 'user') || [];
        const lastUserMsg = userMessages[userMessages.length - 1];
        return lastUserMsg?.content?.toLowerCase()?.trim() || null;
    } catch {
        return null;
    }
};

/** Content type the chat sends back when a SkillPermission card is answered. */
const SKILL_PERMISSION_RESPONSE_TYPE = 'skill_permission_response';
const FORM_RESPONSE_TYPE = 'form_response';

// Matched loosely rather than by equality: an unrecognized prompt falls through
// to word-overlap matching against the recordings, which would silently replay
// an unrelated conversation instead of the capability-driven scenario.
const SKILL_PERMISSION_PROMPT_PATTERN = /skill permission/;
const DELETE_VOLUME_PROMPT_PATTERN = /\bdelete\b.*\bvolume\b/;
const MODE_CONTEXT = 'mode';
const SKILL_PERMISSION_SESSION = 'skill-permission';
const DELETE_VOLUME_SESSION = 'delete-volume';
const VALID_CAPABILITY_LEVELS = new Set(['off', 'ask', 'allow']);

/** The two capabilities the user controls in the panel, and their context names. */
const CAPABILITY_CONTEXT_BY_KEY = {
    analyzer: 'capability:analyzer',
    storage_management: 'capability:storage_management'
};

/**
 * Converts the request context array into a name/value lookup.
 * Unknown and malformed entries are ignored so recordings remain permissive.
 */
const parseRequestContext = (request) => (request?.context || []).reduce((result, entry) => {
    if (entry && typeof entry.name === 'string') {
        result[entry.name] = entry.value;
    }
    return result;
}, {});

/** An unset or unrecognized level is treated as "ask", the safest gate. */
const getCapabilityLevel = (context, capabilityKey) => {
    const level = context[CAPABILITY_CONTEXT_BY_KEY[capabilityKey]];
    return VALID_CAPABILITY_LEVELS.has(level) ? level : 'ask';
};

/**
 * Reads a SkillPermission answer envelope.
 *
 * The chat answers a permission card with a JSON envelope rather than prose. It
 * names the button that was pressed and the card it belongs to, so the scenario
 * can tell which of several open gates is being answered.
 *
 * @param {string} content - A user message's content.
 * @returns {{action: string, permissionId: string|null, capabilityKey: string|null,
 *            skillId: string|null}|null}
 */
const extractPermissionAnswer = (content) => {
    if (typeof content !== 'string' || !content.includes(SKILL_PERMISSION_RESPONSE_TYPE)) return null;
    try {
        const parsed = JSON.parse(content);
        if (parsed?.type !== SKILL_PERMISSION_RESPONSE_TYPE || !parsed.action) return null;

        return {
            action: parsed.action,
            permissionId: parsed.permissionId || null,
            capabilityKey: parsed.capabilityKey || null,
            skillId: parsed.skillId || null
        };
    } catch {
        return null;
    }
};

/**
 * The button a SkillPermission answer pressed, for HAR recordings that hold one
 * turn per branch (approve, deny, enable_ask, enable_always, cancel).
 *
 * @param {string} content - A user message's content.
 * @returns {string|null}
 */
const extractPermissionAction = (content) => extractPermissionAnswer(content)?.action || null;

/**
 * Reads the DynamicForm answer envelope sent by ConfirmationBar.
 */
const extractFormAnswer = (content) => {
    if (typeof content !== 'string' || !content.includes(FORM_RESPONSE_TYPE)) return null;
    try {
        const parsed = JSON.parse(content);
        if (parsed?.type !== FORM_RESPONSE_TYPE || !parsed.action) return null;
        return {
            action: parsed.action,
            workflowId: parsed.workflowId || null,
            layoutName: parsed.layoutName || null,
            payload: parsed.payload || {}
        };
    } catch {
        return null;
    }
};

/**
 * Splits a HAR recording into an array of conversation turns.
 *
 * Each turn contains:
 *   - userPrompt: the user's message that initiates this turn (lowercased)
 *   - permissionAction: the SkillPermission button this turn answers, if any
 *   - receiveMessages: the server responses to replay for this turn
 *
 * The recording is segmented at each "send" entry that contains a messages array.
 * "receive" entries between two such sends belong to the earlier turn.
 * Ping sends (type: "ping") are skipped — they are not turn boundaries.
 *
 * Example for create_vol.json (4 sends):
 *   Turn 0: "create a volume..." → [receive responses for turn 0]
 *   Turn 1: "volume name vol_1..."→ [receive responses for turn 1]
 *   Turn 2: "working env ID..."  → [receive responses for turn 2]
 *   Turn 3: "Yes"                → [receive responses for turn 3]
 *
 * @param {Array} recording - The full HAR-recorded message array.
 * @returns {Array<{userPrompt: string, permissionAction: string|null, receiveMessages: Array}>}
 */
const parseConversationTurns = (recording) => {
    const turns = [];
    let currentTurn = null;

    for (const entry of recording) {
        if (isUserMessage(entry)) {
            // Start a new turn — any previous turn is now complete
            if (currentTurn) {
                turns.push(currentTurn);
            }
            const userPrompt = extractLastUserContent(entry);
            currentTurn = {
                userPrompt,
                permissionAction: extractPermissionAction(userPrompt),
                receiveMessages: []
            };
        } else if (entry.type === 'receive' && currentTurn) {
            currentTurn.receiveMessages.push(entry);
        }
        // Ping sends and other non-user sends are ignored as turn boundaries
    }

    // Push the final turn
    if (currentTurn) {
        turns.push(currentTurn);
    }

    return turns;
};

// ============================================================================
// Conversation Map
// ============================================================================

/**
 * @typedef {Object} ConversationEntry
 * @property {string} initialPrompt - The first user message (key for lookup)
 * @property {Array<{userPrompt: string, receiveMessages: Array}>} turns - Ordered turns
 * @property {boolean} isMultiTurn - Whether this conversation has more than one turn
 */

/**
 * Builds a map of initial user prompt → conversation entry for all chat recordings.
 * Key: lowercased first user message for case-insensitive matching.
 * Value: ConversationEntry with all turns for multi-turn replay.
 * @returns {Map<string, ConversationEntry>}
 */
const buildConversationMap = () => {
    const map = new Map();

    allChatRecordings.forEach((recording) => {
        const turns = parseConversationTurns(recording);
        if (turns.length === 0) return;

        const initialPrompt = turns[0].userPrompt;
        if (!initialPrompt) return;

        const entry = {
            initialPrompt,
            turns,
            isMultiTurn: turns.length > 1
        };

        map.set(initialPrompt, entry);
        console.log(
            `AI Chat: Registered conversation — "${initialPrompt.substring(0, 60)}..." ` +
            `(${turns.length} turn(s), ${turns.reduce((sum, t) => sum + t.receiveMessages.length, 0)} messages)`
        );
    });

    console.log(`AI Chat: ${map.size} conversation(s) loaded`);
    return map;
};

/** @type {Map<string, ConversationEntry>} */
let conversationMap = null;

/** Key used to look up the default fallback conversation (welcome.json) */
const FALLBACK_KEY = extractLastUserContent(chatWelcome.find(e => isUserMessage(e)));

/**
 * Sets the conversation map (used by server lifecycle).
 * @param {Map<string, ConversationEntry>|null} map
 */
const setConversationMap = (map) => {
    conversationMap = map;
};

// ============================================================================
// Message Matching
// ============================================================================

/**
 * Common phrases that should trigger the welcome conversation.
 * These are greetings or help requests that indicate the user wants an introduction.
 */
const WELCOME_PHRASES = [
    'hi',
    'hello',
    'hola',
    'hey',
    'what can you help me with',
    'what can you help me with?',
    'what can you do',
    'what can you do?',
    'help'
];

/**
 * Finds the best matching conversation entry for a given user message.
 * Used to identify which recorded conversation to start.
 * Matching strategy (in order of priority):
 *   0. Welcome phrases — triggers welcome.json for greetings/help requests
 *   1. Exact match (case-insensitive)
 *   2. Recorded prompt contained within user message, or vice versa
 *   3. Word-overlap similarity — picks the recording with the most shared words
 *   4. Falls back to the first registered conversation
 *
 * @param {string} userMessage - The user's incoming message.
 * @returns {ConversationEntry|null} - The matched conversation entry.
 */
const findConversation = (userMessage) => {
    if (!conversationMap || conversationMap.size === 0) {
        console.warn('AI Chat: No conversations loaded');
        return null;
    }

    const normalizedInput = userMessage.toLowerCase().trim();

    // 0. Check for welcome phrases
    if (WELCOME_PHRASES.includes(normalizedInput)) {
        const welcomeConv = FALLBACK_KEY && conversationMap.get(FALLBACK_KEY);
        if (welcomeConv) {
            console.log('AI Chat: Welcome phrase detected, using welcome conversation');
            return welcomeConv;
        }
    }

    // 1. Exact match
    if (conversationMap.has(normalizedInput)) {
        console.log('AI Chat: Exact match found');
        return conversationMap.get(normalizedInput);
    }

    // 2. Substring match
    for (const [prompt, entry] of conversationMap) {
        if (normalizedInput.includes(prompt) || prompt.includes(normalizedInput)) {
            console.log(`AI Chat: Substring match — "${prompt.substring(0, 50)}..."`);
            return entry;
        }
    }

    // 3. Word-overlap similarity
    const inputWords = new Set(normalizedInput.split(/\s+/));
    let bestMatch = null;
    let bestScore = 0;

    for (const [prompt, entry] of conversationMap) {
        const promptWords = prompt.split(/\s+/);
        const score = promptWords.filter(word => inputWords.has(word)).length;
        if (score > bestScore) {
            bestScore = score;
            bestMatch = entry;
        }
    }

    if (bestMatch && bestScore > 0) {
        console.log(`AI Chat: Best word-overlap match (score: ${bestScore})`);
        return bestMatch;
    }

    // 4. Fallback — welcome conversation
    const fallback = (FALLBACK_KEY && conversationMap.get(FALLBACK_KEY)) || conversationMap.values().next().value;
    console.log('AI Chat: No match found, using fallback (welcome) conversation');
    return fallback;
};

// ============================================================================
// Replay Engine
// ============================================================================

/** Track active replay per socket to allow concurrent conversations */
let activeReplayId = 0;

/**
 * Resets the active replay ID counter (used by server lifecycle on shutdown).
 */
const resetActiveReplayId = () => {
    activeReplayId = 0;
    skillPermissionSessionId = 0;
};

/**
 * Computes the delay between consecutive chat messages based on their recorded timestamps.
 * Uses the time difference from the HAR recording, clamped to a max of 2 seconds
 * to keep the offline replay responsive.
 * @param {number} currentTime - The timestamp of the current message (seconds).
 * @param {number} nextTime - The timestamp of the next message (seconds).
 * @returns {number} - The delay in milliseconds.
 */
const getReplayDelay = (currentTime, nextTime) => {
    if (!nextTime || !currentTime) return 50;
    const diffMs = Math.max(0, (nextTime - currentTime) * 1000);
    // Clamp to max 2s to keep the demo snappy, min 20ms for rapid sequences
    return Math.min(Math.max(diffMs, 20), 2000);
};

/**
 * Replays a single turn's receive messages over the WebSocket connection
 * with realistic timing based on the original HAR timestamps.
 * @param {Object} socket - The WebSocket connection object.
 * @param {Array} receiveMessages - The server response messages for this turn.
 * @param {number} replayId - The replay session ID for cancellation support.
 * @returns {Promise<boolean>} - true if completed, false if cancelled.
 */
const replayTurn = async (socket, receiveMessages, replayId) => {
    for (let i = 0; i < receiveMessages.length; i++) {
        // If a newer replay was started, stop this one
        if (replayId !== activeReplayId) {
            console.log('AI Chat: Replay cancelled (new conversation started)');
            return false;
        }

        const message = receiveMessages[i];
        const nextMessage = receiveMessages[i + 1];

        console.log('AI Chat: Sending message:', message.data?.substring?.(0, 80) || message.data);
        socket.send(message.data);

        const delay = getReplayDelay(message.time, nextMessage?.time);
        await sleep(delay);
    }

    return true;
};

// ============================================================================
// Dynamic SkillPermission Scenario
// ============================================================================

let skillPermissionSessionId = 0;

/**
 * The simulated task: look at the cluster, then create a volume on it. Each step
 * names the skill and the capability that gates it, so the single prompt covers
 * every combination of the two capability levels the user picks in the panel.
 *
 * Read-only mode drops the agent-only step. The chat forces Modify storage off
 * whenever agent mode is off, so a Modify storage card there would report the
 * mode rather than anything the user chose.
 */
const SCENARIO_STEPS = [
    {
        capabilityKey: 'analyzer',
        capabilityName: 'Analyze storage',
        skillId: 'analyze_capacity',
        skillName: 'Analyze capacity',
        result: 'Cluster prod-01 is at 68% capacity, with 4.2 TiB free.'
    },
    {
        capabilityKey: 'analyzer',
        capabilityName: 'Analyze storage',
        skillId: 'check_cluster_health',
        skillName: 'Check cluster health',
        result: 'All four nodes on prod-01 report healthy.'
    },
    {
        capabilityKey: 'storage_management',
        capabilityName: 'Modify storage',
        skillId: 'create_volume',
        skillName: 'Create volume',
        agentOnly: true,
        result: 'Created demo volume vol_demo_01 (100 GiB) on cluster prod-01.'
    }
];

const DELETE_VOLUME_STEPS = [
    {
        capabilityKey: 'analyzer',
        capabilityName: 'Analyze storage',
        skillId: 'list_volumes',
        skillName: 'List volumes',
        result: 'I went through your connected clusters and collected the volumes you can delete.'
    },
    {
        capabilityKey: 'storage_management',
        capabilityName: 'Modify storage',
        skillId: 'prepare_volume_deletion',
        skillName: 'Prepare volume deletion',
        result: 'I checked that those volumes can be removed safely and prepared the deletion request.'
    }
];

const CARD_GAP_SECONDS = 0.25;

// A running card is replaced by its completed one under the same permissionId,
// so without a dwell the loader would be swapped out before it is ever seen.
const LOADER_DWELL_SECONDS = 1.2;
const IN_PROGRESS_CARD_STATUSES = new Set(['using', 'approved_using']);

const createReceiveEntry = (event, offsetSeconds) => ({
    type: 'receive',
    time: (Date.now() / 1000) + offsetSeconds,
    opcode: 1,
    data: JSON.stringify({ ...event, timestamp: Date.now() + Math.round(offsetSeconds * 1000) })
});

const createRunEvents = (session, cards, text, extraEvents = []) => {
    const runId = `${session.threadId}-run-${++session.runNumber}`;
    const events = [
        createReceiveEntry({
            type: 'RUN_STARTED',
            data: { threadId: session.threadId, runId }
        }, 0)
    ];

    let offset = 0.05;
    cards.forEach((data) => {
        events.push(createReceiveEntry({
            type: 'CUSTOM',
            data: {
                name: 'UIDescriptor',
                value: {
                    component: 'SkillPermission',
                    data: { re_seq: 'INSTANT', ...data }
                }
            }
        }, offset));
        offset += IN_PROGRESS_CARD_STATUSES.has(data.status)
            ? LOADER_DWELL_SECONDS
            : CARD_GAP_SECONDS;
    });

    if (text) {
        const messageId = `${runId}-message`;
        events.push(
            createReceiveEntry({
                type: 'TEXT_MESSAGE_START',
                data: { messageId, role: 'assistant' }
            }, offset),
            createReceiveEntry({
                type: 'TEXT_MESSAGE_CONTENT',
                data: { messageId, delta: text }
            }, offset + 0.01),
            createReceiveEntry({
                type: 'TEXT_MESSAGE_END',
                data: { messageId }
            }, offset + 0.02)
        );
        offset += 0.05;
    }

    extraEvents.forEach((event) => {
        events.push(createReceiveEntry(event, offset));
        offset += CARD_GAP_SECONDS;
    });

    events.push(createReceiveEntry({
        type: 'RUN_FINISHED',
        data: {
            threadId: session.threadId,
            runId,
            result: {
                agent: session.agent || 'Coordinator',
                intent: session.intent || 'sm_create_volume'
            }
        }
    }, offset));

    return events;
};

// A capability gate is raised once per capability; a skill gate once per skill.
// Separate ids give every skill its own card, while a card's own progression
// (running, then done) keeps updating in place.
const capabilityPermissionId = (session, capabilityKey) =>
    `perm_sim_${session.id}_cap_${capabilityKey}`;

const skillPermissionId = (session, skillId) =>
    `perm_sim_${session.id}_skill_${skillId}`;

const capabilityCard = (session, step, status) => ({
    permissionId: capabilityPermissionId(session, step.capabilityKey),
    status,
    capabilityKey: step.capabilityKey,
    capabilityName: step.capabilityName,
    ...(status === 'capability_off' && {
        description: 'Your task requires this capability. Enable it to continue.'
    })
});

const skillCard = (session, step, status) => ({
    permissionId: skillPermissionId(session, step.skillId),
    status,
    skillId: step.skillId,
    skillName: step.skillName,
    ...(status === 'approval_required' && {
        capabilityKey: step.capabilityKey,
        capabilityName: step.capabilityName
    })
});

const replaySkillPermissionRun = async (socket, session, cards, text, extraEvents = []) => {
    const replayId = ++activeReplayId;
    session.replayId = replayId;
    return replayTurn(socket, createRunEvents(session, cards, text, extraEvents), replayId);
};

const finishSkillPermissionSession = (socket) => {
    socketSessions.delete(socket);
};

const DELETE_VOLUME_LAYOUT_NAME = 'delete_volume';
const DELETE_VOLUME_FORM_CELL = 'delete-volume-form';
const DELETE_VOLUME_ACTIONS_CELL = 'delete-volume-actions';

const deleteVolumeLayoutId = (session) => `delete-volume-layout-${session.id}`;

/**
 * The form arrives the way a real backend sends one: a layout that owns the
 * workflow, with the fields in a grid cell and the accept/cancel bar pinned to
 * the bottom of the canvas.
 */
const createDeleteVolumeLayoutEvent = (session) => ({
    type: 'CUSTOM',
    data: {
        name: 'LayoutGrid',
        value: {
            id: DELETE_VOLUME_LAYOUT_NAME,
            name: DELETE_VOLUME_LAYOUT_NAME,
            layoutId: deleteVolumeLayoutId(session),
            workflowId: session.workflowId,
            skillName: 'custom_sm_workflow',
            title: 'Delete a volume',
            layout: [
                { i: DELETE_VOLUME_FORM_CELL, component: 'DynamicForm' },
                {
                    i: DELETE_VOLUME_ACTIONS_CELL,
                    component: 'ConfirmationBar',
                    ds: 'ui',
                    pin: 'bottom',
                    props: {
                        confirmLabel: 'Confirm',
                        cancelLabel: 'Cancel',
                        destructive: true,
                        destructiveField: 'volume'
                    }
                }
            ]
        }
    }
});

/**
 * Each dropdown is populated from the one above it, so the cluster decides which
 * storage VMs exist and the storage VM decides which volumes can be deleted.
 */
const createDeleteVolumeFormEvent = (session) => ({
    type: 'CUSTOM',
    data: {
        name: 'UIDescriptor',
        value: {
            layoutId: deleteVolumeLayoutId(session),
            cellId: DELETE_VOLUME_FORM_CELL,
            component: 'DynamicForm',
            data: {
                id: DELETE_VOLUME_FORM_CELL,
                heading: 'Delete a volume',
                text: 'Deleting a volume permanently removes it and every file it holds. '
                    + 'Choose the volume you want removed — nothing is deleted until you confirm.',
                fields: [
                    {
                        type: 'dropdown',
                        label: 'Cluster',
                        name: 'cluster',
                        placeholder: 'Select a cluster',
                        isRequired: true,
                        isSearchable: true,
                        options: listSimulatorClusters().map(({ id, name }) => ({
                            label: name,
                            value: id
                        }))
                    },
                    {
                        type: 'dropdown',
                        label: 'Storage VM',
                        name: 'svm',
                        placeholder: 'Select a storage VM',
                        isRequired: true,
                        isSearchable: true,
                        options: [],
                        apiURL: `${SIMULATOR_SVM_LOOKUP_PATH}?clusterId={{form.cluster}}`,
                        watch: ['cluster'],
                        watchMode: 'all'
                    },
                    {
                        type: 'dropdown',
                        label: 'Volume',
                        name: 'volume',
                        placeholder: 'Select a volume',
                        isRequired: true,
                        isSearchable: true,
                        options: [],
                        apiURL: `${SIMULATOR_VOLUME_LOOKUP_PATH}?svmId={{form.svm}}`,
                        watch: ['svm'],
                        watchMode: 'all'
                    }
                ]
            }
        }
    }
});

const openDeleteVolumeForm = async (socket, session, cards) => {
    session.pending = null;
    session.stage = 'awaiting_form';
    return replaySkillPermissionRun(
        socket,
        session,
        cards,
        [
            ...session.resultLines,
            'I opened the delete-volume form on the right. Pick a cluster, then one of its storage VMs, '
            + 'then the volume to remove. Selecting Confirm asks you to type "delete" before I do anything.'
        ].join(' '),
        [createDeleteVolumeLayoutEvent(session), createDeleteVolumeFormEvent(session)]
    );
};

/**
 * Walks the remaining steps, collecting cards until one needs an answer.
 *
 * Steps whose capability is on "allow" run without asking, so a stretch of them
 * is replayed as one run. The walk stops at the first step gated behind "off"
 * (the capability gate) or "ask" (a per-skill approval), and the answer resumes
 * it from the same place. The answer's own cards are passed in as a prefix so
 * they share the run with whatever comes next.
 *
 * @param {Object} socket
 * @param {Object} session
 * @param {{cards?: Array, lines?: Array}} [prefix] - Cards and text from the
 *        answer that resumed the walk.
 */
const advanceSkillPermissionScenario = async (socket, session, prefix = {}) => {
    const cards = [...(prefix.cards || [])];
    session.resultLines.push(...(prefix.lines || []));

    while (session.stepIndex < session.steps.length) {
        const step = session.steps[session.stepIndex];
        const level = session.levels[step.capabilityKey];

        if (level === 'off') {
            session.pending = { type: 'capability', capabilityKey: step.capabilityKey };
            cards.push(capabilityCard(session, step, 'capability_off'));
            return replaySkillPermissionRun(socket, session, cards, '');
        }

        if (level === 'ask') {
            session.pending = { type: 'skill', skillId: step.skillId };
            cards.push(skillCard(session, step, 'approval_required'));
            return replaySkillPermissionRun(socket, session, cards, '');
        }

        cards.push(skillCard(session, step, 'using'), skillCard(session, step, 'used'));
        session.resultLines.push(step.result);
        session.stepIndex += 1;
    }

    session.pending = null;

    if (session.kind === DELETE_VOLUME_SESSION) {
        return openDeleteVolumeForm(socket, session, cards);
    }

    // The last answer may have completed an empty scenario, leaving nothing to
    // render or say.
    if (cards.length === 0 && session.resultLines.length === 0) {
        finishSkillPermissionSession(socket);
        return true;
    }

    const completed = await replaySkillPermissionRun(
        socket,
        session,
        cards,
        session.resultLines.join(' ')
    );
    finishSkillPermissionSession(socket);
    return completed;
};

const startSkillPermissionScenario = async (socket, request) => {
    const id = ++skillPermissionSessionId;
    const context = parseRequestContext(request);
    const mode = context[MODE_CONTEXT] || 'act';
    const isAgentMode = mode !== 'discuss';
    const levels = {
        analyzer: getCapabilityLevel(context, 'analyzer'),
        storage_management: getCapabilityLevel(context, 'storage_management')
    };
    const steps = SCENARIO_STEPS.filter(step => isAgentMode || !step.agentOnly);

    // Which cards appear is decided entirely by these values, so log them: mode
    // forces Modify storage off no matter what the panel shows.
    console.log(
        `AI Chat: SkillPermission scenario — mode "${mode}", Analyze storage `
        + `"${levels.analyzer}", Modify storage "${levels.storage_management}", `
        + `${steps.length} step(s)`
    );

    const session = {
        kind: SKILL_PERMISSION_SESSION,
        id,
        levels,
        steps,
        stepIndex: 0,
        pending: null,
        resultLines: [],
        threadId: `skill-permission-thread-${id}`,
        runNumber: 0,
        replayId: null
    };
    socketSessions.set(socket, session);

    return advanceSkillPermissionScenario(socket, session);
};

const startDeleteVolumeScenario = async (socket, request) => {
    const id = ++skillPermissionSessionId;
    const context = parseRequestContext(request);
    const mode = context[MODE_CONTEXT] || 'discuss';
    const session = {
        kind: DELETE_VOLUME_SESSION,
        id,
        levels: {
            analyzer: getCapabilityLevel(context, 'analyzer'),
            storage_management: getCapabilityLevel(context, 'storage_management')
        },
        steps: DELETE_VOLUME_STEPS,
        stepIndex: 0,
        pending: null,
        resultLines: [],
        stage: 'capabilities',
        workflowId: `workflow-delete-volume-${id}`,
        threadId: `delete-volume-thread-${id}`,
        runNumber: 0,
        replayId: null,
        intent: 'sm_delete_volume',
        agent: 'Storage Management Agent'
    };

    if (mode !== 'act') {
        const text = [
            '🔒 **Read/Write mode required**',
            'Deleting a volume destroys the volume and all of its data, so it counts as a write operation.',
            'You’re in **Read-only mode** right now. Switch to **Read/Write** in the dropdown under the chat box, then ask me again.',
            '**Read-only mode**: search, query, and explore  \n'
            + '**Read/Write mode**: everything above, plus changes to your storage'
        ].join('\n\n');
        return replaySkillPermissionRun(socket, session, [], text);
    }

    console.log(
        `AI Chat: Delete-volume scenario — Analyze storage "${session.levels.analyzer}", `
        + `Modify storage "${session.levels.storage_management}"`
    );
    socketSessions.set(socket, session);
    return advanceSkillPermissionScenario(socket, session);
};

/** The card the scenario is currently waiting on. */
const pendingPermissionId = (session, step) => (session.pending?.type === 'capability'
    ? capabilityPermissionId(session, step.capabilityKey)
    : skillPermissionId(session, step.skillId));

/**
 * Applies an answer to the gate the scenario is waiting on.
 *
 * Approving or enabling resumes the walk through the remaining steps; denying or
 * cancelling ends the task there, so the steps behind the refused one never run.
 */
const handleSkillPermissionAnswer = async (socket, session, answer) => {
    const step = session.steps[session.stepIndex];

    if (!step || !session.pending) return false;

    if (answer.permissionId && answer.permissionId !== pendingPermissionId(session, step)) {
        console.log(`AI Chat: SkillPermission answer for "${answer.permissionId}" is not the open card — ignoring`);
        return false;
    }

    session.pending = null;

    if (answer.action === 'cancel') {
        const completion = `${step.capabilityName} stays turned off, so I stopped before running `
            + `${step.skillName}. Nothing has changed.`;
        await replaySkillPermissionRun(
            socket,
            session,
            [capabilityCard(session, step, 'capability_cancelled')],
            [...session.resultLines, completion].join(' ')
        );
        finishSkillPermissionSession(socket);
        return true;
    }

    if (answer.action === 'deny') {
        const completion = `Understood — I skipped ${step.skillName} and stopped there. Nothing has changed.`;
        await replaySkillPermissionRun(
            socket,
            session,
            [skillCard(session, step, 'denied')],
            [...session.resultLines, completion].join(' ')
        );
        finishSkillPermissionSession(socket);
        return true;
    }

    if (answer.action === 'enable_ask' || answer.action === 'enable_always') {
        session.levels[step.capabilityKey] = answer.action === 'enable_ask' ? 'ask' : 'allow';

        return advanceSkillPermissionScenario(socket, session, {
            cards: [capabilityCard(session, step, 'capability_enabled')],
            lines: [`${step.capabilityName} is enabled for this session.`]
        });
    }

    if (answer.action === 'approve') {
        session.stepIndex += 1;

        return advanceSkillPermissionScenario(socket, session, {
            cards: [
                skillCard(session, step, 'approved_using'),
                skillCard(session, step, 'approved_used')
            ],
            lines: [step.result]
        });
    }

    return false;
};

/**
 * The confirmation names exactly what the form's dropdowns reported, so the
 * volume, storage VM, and cluster read back the same as the user picked them.
 */
const deleteVolumeCompletionText = (payload = {}) => {
    const volume = payload.volume ? `**${payload.volume}**` : 'the selected volume';
    const location = [
        payload.svm && `storage VM **${payload.svm}**`,
        payload.cluster && `cluster **${payload.cluster}**`
    ].filter(Boolean).join(' on ');

    return [
        `Deleted ${volume}${location ? ` from ${location}` : ''}.`,
        'The volume was unmounted, taken offline, and removed along with the data it held. '
        + 'This cannot be undone.'
    ].join('\n\n');
};

const handleDeleteVolumeFormAnswer = async (socket, session, answer) => {
    if (session.stage !== 'awaiting_form') return false;
    if (answer.workflowId && answer.workflowId !== session.workflowId) {
        console.log(`AI Chat: Form answer for "${answer.workflowId}" is not the open delete workflow — ignoring`);
        return false;
    }
    if (!['accept', 'cancel'].includes(answer.action)) return false;

    session.stage = 'completed';
    const text = answer.action === 'accept'
        ? deleteVolumeCompletionText(answer.payload)
        : 'Cancelled — no volume was deleted and your storage is unchanged. '
            + 'Ask me again whenever you are ready.';

    await replaySkillPermissionRun(socket, session, [], text);
    finishSkillPermissionSession(socket);
    return true;
};

// ============================================================================
// Active Session Tracking
// ============================================================================

/**
 * Per-socket session state for multi-turn conversations.
 * Tracks which conversation is active and which turn to replay next.
 * @type {Map<Object, {conversation: ConversationEntry, currentTurnIndex: number, replayId: number}>}
 */
const socketSessions = new Map();

/**
 * Looks up a conversation by its exact opening prompt.
 * @param {string} userContent - The user's message content.
 * @returns {ConversationEntry|null}
 */
const findConversationByExactPrompt = (userContent) =>
    (conversationMap && conversationMap.get(userContent.toLowerCase().trim())) || null;

/**
 * Picks which turn of the active conversation to replay next.
 *
 * Recordings are normally linear, so the next turn is the one after the current.
 * SkillPermission recordings are the exception: the chat answers a permission
 * card with a JSON envelope naming the button that was pressed, so the recording
 * holds one turn per button and the reply selects the branch. That is what makes
 * Approve and Deny lead to different responses from the same card.
 *
 * @param {Object} session - The socket's session state.
 * @param {string} userContent - The user's message content.
 * @returns {number} - Index of the turn to replay.
 */
const selectNextTurnIndex = (session, userContent) => {
    const action = extractPermissionAction(userContent);

    if (action) {
        const branch = session.conversation.turns.findIndex(turn => turn.permissionAction === action);
        if (branch !== -1) {
            console.log(`AI Chat: SkillPermission answer "${action}" — replaying branch turn ${branch + 1}`);
            return branch;
        }
    }

    return session.currentTurnIndex + 1;
};

/**
 * Handles an incoming user message on a socket.
 * - For the first message: matches to a conversation and replays turn 0.
 * - For subsequent messages in a multi-turn conversation: advances to the next turn,
 *   or to the branch that answers a SkillPermission card.
 * - If the conversation is exhausted, sends a completion message.
 *
 * @param {Object} socket - The WebSocket connection object.
 * @param {string} userContent - The user's message content.
 * @param {Object|null} request - Full chat request, including capability context.
 */
const handleUserMessage = async (socket, userContent, request = null) => {
    const normalizedContent = userContent.toLowerCase().trim();
    const permissionAnswer = extractPermissionAnswer(userContent);
    const formAnswer = extractFormAnswer(userContent);

    // This scenario is generated from the current capability context rather than
    // replayed from a fixed HAR. Re-entering the prompt always starts a fresh run.
    if (!permissionAnswer && !formAnswer && DELETE_VOLUME_PROMPT_PATTERN.test(normalizedContent)) {
        socketSessions.delete(socket);
        await startDeleteVolumeScenario(socket, request);
        return;
    }
    if (!permissionAnswer && !formAnswer && SKILL_PERMISSION_PROMPT_PATTERN.test(normalizedContent)) {
        socketSessions.delete(socket);
        await startSkillPermissionScenario(socket, request);
        return;
    }

    const session = socketSessions.get(socket);

    if (session?.kind === SKILL_PERMISSION_SESSION) {
        if (permissionAnswer) {
            await handleSkillPermissionAnswer(socket, session, permissionAnswer);
        }
        return;
    }

    if (session?.kind === DELETE_VOLUME_SESSION) {
        if (permissionAnswer) {
            await handleSkillPermissionAnswer(socket, session, permissionAnswer);
        } else if (formAnswer) {
            await handleDeleteVolumeFormAnswer(socket, session, formAnswer);
        }
        return;
    }

    if (session) {
        // Typing another recording's opening prompt starts that conversation
        // rather than advancing this one, so branch scenarios can be replayed one
        // after another without clearing the chat first.
        const restart = findConversationByExactPrompt(userContent);
        if (restart && restart !== session.conversation) {
            console.log('AI Chat: Opening prompt of another conversation — switching');
            socketSessions.delete(socket);
            await handleUserMessage(socket, userContent);
            return;
        }

        // ── Multi-turn: advance to next turn ──
        const nextIndex = selectNextTurnIndex(session, userContent);

        if (nextIndex >= session.conversation.turns.length) {
            console.log('AI Chat: Conversation complete — no more turns');
            socketSessions.delete(socket);
            return;
        }

        const nextTurn = session.conversation.turns[nextIndex];
        // Branch turns are addressed by name, not by position, so reaching the
        // last one does not mean the conversation is over.
        const isBranch = Boolean(nextTurn.permissionAction);
        session.currentTurnIndex = nextIndex;
        const replayId = ++activeReplayId;
        session.replayId = replayId;

        console.log(`AI Chat: Multi-turn — replaying turn ${nextIndex + 1}/${session.conversation.turns.length}`);
        const completed = await replayTurn(socket, nextTurn.receiveMessages, replayId);

        // If this was the last turn, clean up the session
        if (completed && !isBranch && nextIndex >= session.conversation.turns.length - 1) {
            console.log('AI Chat: Multi-turn conversation fully replayed');
            socketSessions.delete(socket);
        } else if (completed && session.conversation.isMultiTurn) {
            console.log('AI Chat: Turn complete — waiting for user input...');
        }
    } else {
        // ── First message: find matching conversation ──
        const conversation = findConversation(userContent);
        if (!conversation) {
            socket.send(JSON.stringify({
                type: 'RUN_ERROR',
                timestamp: Date.now(),
                data: { message: 'No recorded conversation matches this prompt', code: 'NO_MATCH' }
            }));
            return;
        }

        const replayId = ++activeReplayId;

        // Start session tracking for multi-turn
        const newSession = {
            conversation,
            currentTurnIndex: 0,
            replayId
        };
        socketSessions.set(socket, newSession);

        const firstTurn = conversation.turns[0];
        console.log(
            `AI Chat: Starting conversation — "${conversation.initialPrompt.substring(0, 50)}..." ` +
            `(turn 1/${conversation.turns.length})`
        );

        const completed = await replayTurn(socket, firstTurn.receiveMessages, replayId);

        if (completed && !conversation.isMultiTurn) {
            // Single-turn — done
            console.log('AI Chat: Single-turn conversation replayed');
            socketSessions.delete(socket);
        } else if (completed) {
            console.log('AI Chat: Turn 1 complete — waiting for user input...');
        }
    }
};

export {
    buildConversationMap,
    setConversationMap,
    resetActiveReplayId,
    socketSessions,
    handleUserMessage
};
